import React, { forwardRef, memo, useCallback, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import EditorJS from '@editorjs/editorjs';
import type { API, BlockMutationEvent, OutputBlockData, OutputData, ToolConstructable } from '@editorjs/editorjs';
import Paragraph from '@editorjs/paragraph';
import Header from '@editorjs/header';
import ImageTool from '@editorjs/image';
import type { ImageToolData } from '@editorjs/image/dist/types/types';
import { imageService } from '../../api/imageService';
import type { PortalArticlePayload, PortalContentBlock } from '../../portal/portalDraftEnvelope';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function stripHtml(html: string): string {
  if (!html.trim()) return '';
  const d = document.createElement('div');
  d.innerHTML = html;
  return (d.textContent || d.innerText || '').replace(/\u00a0/g, ' ');
}

function plainToParagraphHtml(text: string): string {
  const t = text.trim();
  if (!t) return '';
  const chunks = t.split(/\n{2,}/);
  return chunks
    .map((chunk) => {
      const esc = escapeHtml(chunk).replace(/\n/g, '<br>');
      return `<p>${esc}</p>`;
    })
    .join('');
}

function inferHeaderLevel(html: string): number {
  const m = html.match(/<h([1-6])\b/i);
  if (m) return Math.min(6, Math.max(1, parseInt(m[1], 10)));
  return 2;
}

function portalBlockToEditorBlock(b: PortalContentBlock): OutputBlockData | null {
  const raw = (b.html || b.text || '').trim();
  const t = (b.type || 'paragraph').toLowerCase();

  if (t === 'image') {
    const src = (b.src || '').trim();
    if (!src) return null;
    return {
      type: 'image',
      data: {
        file: { url: src },
        caption: (b.description || '').trim(),
        withBorder: false,
        withBackground: false,
        stretched: false,
      },
    };
  }

  if (t === 'subtitle') {
    const text = raw.includes('<') ? raw : `<h4>${escapeHtml(raw)}</h4>`;
    const level = inferHeaderLevel(text);
    const safeLevel = level >= 4 ? level : 4;
    return { type: 'subtitle', data: { text, level: safeLevel } };
  }

  if (t === 'header' || t === 'heading') {
    let level = raw ? inferHeaderLevel(raw) : 2;
    if (level >= 4) level = 3;
    if (level < 2) level = 2;
    const text = raw.includes('<') ? raw : `<h${level}>${escapeHtml(raw)}</h${level}>`;
    return { type: 'header', data: { text, level } };
  }

  if (t === 'caption') {
    const text = raw.includes('<') ? raw : plainToParagraphHtml(raw) || `<p>${escapeHtml(raw)}</p>`;
    return { type: 'caption', data: { text } };
  }

  const text = raw.includes('<') ? raw : plainToParagraphHtml(raw) || `<p>${escapeHtml(raw)}</p>`;
  return { type: 'paragraph', data: { text } };
}

/** Hydrate Editor.js from portal article body fields (legacy `content` or `content_blocks`). */
export function portalArticleBodyToEditorData(
  body: Pick<PortalArticlePayload, 'content' | 'content_blocks'>
): OutputData {
  const rawBlocks = body.content_blocks || [];
  const kept = rawBlocks.filter((b) => {
    const t = (b.type || '').toLowerCase();
    if (t === 'image') return Boolean((b.src || '').trim());
    return Boolean((b.text || b.html || '').trim());
  });

  if (kept.length) {
    const blocks = kept.map(portalBlockToEditorBlock).filter(Boolean) as OutputBlockData[];
    return { time: Date.now(), blocks };
  }

  const c = (body.content || '').trim();
  if (c) {
    const text = plainToParagraphHtml(c) || `<p>${escapeHtml(c)}</p>`;
    return { time: Date.now(), blocks: [{ type: 'paragraph', data: { text } }] };
  }
  return { time: Date.now(), blocks: [] };
}

function editorDataToPortalBody(data: OutputData): { content: string; content_blocks: PortalContentBlock[] } {
  const content_blocks: PortalContentBlock[] = [];

  for (const block of data.blocks || []) {
    if (block.type === 'image') {
      const d = block.data as ImageToolData;
      const url = (d.file?.url || '').trim();
      if (!url) continue;
      const caption = (d.caption || '').trim();
      content_blocks.push({
        type: 'image',
        src: url,
        description: caption || undefined,
        text: caption || url,
      });
      continue;
    }

    if (block.type === 'header' || block.type === 'subtitle') {
      const d = block.data as { text?: string; level?: number };
      const textHtml = (d.text ?? '').trim();
      if (!textHtml) continue;
      const plain = stripHtml(textHtml).trim();
      if (!plain) continue;
      const portalType = block.type === 'subtitle' ? 'subtitle' : 'header';
      content_blocks.push({ type: portalType, text: plain, html: textHtml });
      continue;
    }

    if (block.type === 'caption' || block.type === 'paragraph') {
      const d = block.data as { text?: string };
      const textHtml = (d.text ?? '').trim();
      if (!textHtml) continue;
      const plain = stripHtml(textHtml).trim();
      if (!plain) continue;
      const portalType = block.type === 'caption' ? 'caption' : 'paragraph';
      content_blocks.push({ type: portalType, text: plain, html: textHtml });
      continue;
    }

    const d = block.data as { text?: string };
    const textHtml = (d.text ?? '').trim();
    if (textHtml.trim()) {
      const plain = stripHtml(textHtml).trim();
      if (plain) {
        content_blocks.push({ type: String(block.type), text: plain, html: textHtml });
      }
    }
  }

  const content = content_blocks
    .map((b) => {
      if (b.type === 'image') return (b.description || b.text || b.src || '').trim();
      return (b.text || '').trim();
    })
    .filter(Boolean)
    .join('\n\n');

  return { content, content_blocks };
}

function resolveUploadedImageUrl(pathOrUrl: string): string {
  const u = (pathOrUrl || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://') || u.startsWith('data:')) return u;
  const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
  if (u.startsWith('/') && base) return `${base}${u}`;
  return u;
}

export type ArticleBodyEditorJsProps = {
  notebookId: string;
  readOnly: boolean;
  seed: Pick<PortalArticlePayload, 'content' | 'content_blocks'>;
  onBlurValidation: () => void;
  onBodyChange: (content: string, content_blocks: PortalContentBlock[]) => void;
};

/**
 * Editor.js owns the holder subtree. If the holder re-renders on every parent `setState`, React reconciles
 * the empty `<div />` and strips Editor.js nodes. This holder is memoized with a constant `true` comparator
 * so it never re-renders after mount while the parent can update freely.
 */
const ArticleBodyEditorStableHolder = memo(
  forwardRef<HTMLDivElement>(function ArticleBodyEditorStableHolderInner(_props, ref) {
    return <div ref={ref} style={{ minHeight: 200, width: '100%' }} />;
  }),
  () => true
);

/**
 * Initializes Editor.js in the stable holder; re-inits when `notebookId` or `readOnly` changes.
 */
function ArticleBodyEditorJsView({
  notebookId,
  readOnly,
  seed,
  onBlurValidation,
  onBodyChange,
}: ArticleBodyEditorJsProps) {
  const holderRef = useRef<HTMLDivElement | null>(null);
  const onBodyChangeRef = useRef(onBodyChange);
  const onBlurValidationRef = useRef(onBlurValidation);
  onBodyChangeRef.current = onBodyChange;
  onBlurValidationRef.current = onBlurValidation;

  const persist = useCallback(async (editor: EditorJS) => {
    try {
      const data = await editor.save();
      const { content, content_blocks } = editorDataToPortalBody(data);
      onBodyChangeRef.current(content, content_blocks);
    } catch {
      /* save can race before isReady in edge cases */
    }
  }, []);

  /**
   * React 18 Strict Mode mounts → unmounts → remounts in dev. Editor.js + async `isReady`/`destroy`
   * races with that and often leaves a blank, non-interactive surface. Deferring init to the next
   * macrotask lets the first (Strict) effect cleanup clear the pending timer so only the final
   * mount constructs the editor (same pattern as react-editor-js Strict Mode discussions).
   */
  useEffect(() => {
    const holder = holderRef.current;
    if (!holder) return undefined;

    let alive = true;
    let editor: EditorJS | null = null;

    const initialData = portalArticleBodyToEditorData(seed);

    const uploader = readOnly
      ? undefined
      : {
          uploadByFile: async (file: File) => {
            const res = await imageService.ingestOgImage({ file });
            return { success: 1 as const, file: { url: resolveUploadedImageUrl(res.url) } };
          },
          uploadByUrl: async (remoteUrl: string) => {
            const res = await imageService.ingestOgImage({ url: remoteUrl.trim() });
            return { success: 1 as const, file: { url: resolveUploadedImageUrl(res.url) } };
          },
        };

    const start = () => {
      if (!alive) return;
      holder.replaceChildren();
      try {
        editor = new EditorJS({
          holder,
          readOnly,
          minHeight: 200,
          autofocus: !readOnly,
          defaultBlock: 'paragraph',
          placeholder: readOnly ? false : 'Write the article body…',
          tools: {
            paragraph: {
              class: Paragraph as unknown as ToolConstructable,
              config: { placeholder: 'Text' },
            },
            caption: {
              class: Paragraph as unknown as ToolConstructable,
              toolbox: { title: 'Image caption' },
              config: { placeholder: 'Image caption' },
            },
            header: {
              class: Header as unknown as ToolConstructable,
              config: { placeholder: 'Heading', levels: [2, 3], defaultLevel: 2 },
            },
            subtitle: {
              class: Header as unknown as ToolConstructable,
              toolbox: { title: 'Subtitle' },
              config: { placeholder: 'Subtitle', levels: [4, 5, 6], defaultLevel: 4 },
            },
            image: {
              class: ImageTool as unknown as ToolConstructable,
              config: {
                endpoints: { byFile: '', byUrl: '' },
                captionPlaceholder: 'Caption (optional)',
                features: { caption: 'optional', border: true, stretch: true, background: false },
                ...(uploader ? { uploader } : {}),
              },
            },
          },
          data: initialData,
          onChange: (_api: API, _event: BlockMutationEvent | BlockMutationEvent[]) => {
            if (editor) void persist(editor);
          },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[ArticleBodyEditorJs] EditorJS constructor failed', err);
        return;
      }

      void editor.isReady.catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[ArticleBodyEditorJs] EditorJS isReady rejected', err);
      });
    };

    const timer = window.setTimeout(start, 0);

    return () => {
      alive = false;
      window.clearTimeout(timer);
      const ed = editor;
      editor = null;
      if (!ed) return;
      void ed.isReady
        .then(() => {
          try {
            if (typeof ed.destroy === 'function') {
              ed.destroy();
            }
          } catch {
            /* holder may already be cleared */
          }
        })
        .catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notebookId, readOnly, persist]);

  const editorDarkSurfaceSx = {
    color: '#fafafa',
    flex: 1,
    minHeight: 200,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    bgcolor: '#171717',
    px: 1.5,
    py: 1,
    overflow: 'auto',
    position: 'relative',
    zIndex: 1,
    pointerEvents: 'auto',
    /* Bootstrap sets global border-box; Editor.js popovers/settings expect content-box in places. */
    '& .codex-editor, & .ce-settings, & .ce-toolbox, & .ce-inline-toolbar': {
      boxSizing: 'content-box',
    },
    '& .codex-editor': {
      color: '#fafafa',
      minHeight: 200,
      '--color-dark': '#fafafa',
      '--grayText': '#a1a1aa',
      '--bg-light': '#27272a',
      '--color-line-gray': '#3f3f46',
      '--color-text-primary': '#fafafa',
      '--color-text-secondary': '#a1a1aa',
    },
    '& .codex-editor__redactor': {
      minHeight: 180,
      paddingBottom: 2,
    },
    '& .ce-block__content': { maxWidth: 'none' },
    '& .ce-toolbar__content': { maxWidth: 'none' },
    '& [contenteditable="true"]': {
      color: '#fafafa',
      caretColor: '#fafafa',
    },
    '& .ce-paragraph, & .ce-paragraph p': { color: '#fafafa' },
    '& .ce-header': { color: '#fafafa' },
    '& .image-tool': {
      '--bg-color': '#3f3f46',
      '--front-color': '#60a5fa',
      '--border-color': '#52525b',
    },
    '& .image-tool__caption[contenteditable="true"]': {
      color: '#fafafa',
    },
    '& .codex-editor [data-placeholder]:empty:before, & .codex-editor [data-placeholder][data-empty=true]:before': {
      color: '#71717a !important',
    },
    '& .ce-toolbar__plus, & .ce-toolbar__settings-btn': {
      color: '#e4e4e7',
    },
    '@media (hover: hover)': {
      '& .ce-toolbar__plus:hover, & .ce-toolbar__settings-btn:hover': {
        backgroundColor: '#27272a',
      },
    },
    '& .ce-toolbar__plus--active, & .ce-toolbar__settings-btn--active': {
      backgroundColor: '#27272a',
    },
  } as const;

  return (
    <Box
      className="article-body-editorjs-root"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          onBlurValidationRef.current();
        }
      }}
      sx={editorDarkSurfaceSx}
    >
      <ArticleBodyEditorStableHolder ref={holderRef} />
    </Box>
  );
}

export const ArticleBodyEditorJs = ArticleBodyEditorJsView;
